import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import {
  adminApi,
  can,
  ORG_LIST_LIMIT,
  type AdminOrg,
  type AdminProblemCard,
  type AdminSolutionCard,
  type StatusOption,
} from '../lib/adminApi'

/**
 * 관리자 검수 화면 — 설계서 A-01(검수 상태머신)의 최소 구현.
 *
 * 목록은 **전 상태**를 보여준다(공개 화면은 published만 본다). 상태 전환 버튼은 서버가
 * 내려준 `allowedNext`만 그린다 — 전이 규칙을 화면에 복제하면 서버와 어긋난다.
 */

/**
 * 필터용 상태 목록. 정본은 서버 `lib/review-status.ts`와 용어 통일표 §3-1이고
 * 여기 있는 건 필터 select를 그리기 위한 사본이다(shared 모듈을 두지 않는 대신).
 * 행에 표시되는 라벨은 사본이 아니라 서버가 준 `reviewStatusLabel`을 쓴다.
 */
const STATUS_FILTER: StatusOption[] = [
  { value: '', label: '전체' },
  { value: 'draft', label: '작성중' },
  { value: 'review_requested', label: '검토요청' },
  { value: 'published', label: '공개' },
  { value: 'revision_needed', label: '수정필요' },
  { value: 'suspended', label: '중단' },
  { value: 'expired', label: '만료' },
]

const TABS = [
  { key: 'solution', label: '해결카드' },
  { key: 'problem', label: '시민 문제' },
  { key: 'org', label: '기업' },
] as const

type TabKey = (typeof TABS)[number]['key']

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    if (error.status === 409) return error.detail[0] ?? '이미 다른 상태로 바뀐 항목입니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminReviewPage() {
  const me = useAdminMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<TabKey>('solution')
  const [status, setStatus] = useState('')
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false)

  const mayPublish = can(me.data, 'card', 'publish')
  const mayWrite = can(me.data, 'card', 'write')

  const logout = useMutation({
    mutationFn: () => adminApi.logout(),
    // **성공했을 때만** 화면을 뜬다. 세션 폐기는 서버만 할 수 있으므로, 요청이 실패했는데
    // 로그인 화면으로 보내면 실제로는 로그인된 채로 "로그아웃했다"고 착각하게 된다.
    onSuccess: () => {
      queryClient.clear()
      void navigate('/admin/login', { replace: true })
    },
  })

  const transition = useMutation({
    mutationFn: (v: { kind: 'solution-cards' | 'problem-cards'; id: string; to: string }) =>
      adminApi.setReviewStatus(v.kind, v.id, v.to),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-cards'] }),
  })

  const orgPublish = useMutation({
    mutationFn: (v: { id: string; isPublished: boolean }) =>
      adminApi.setOrgPublished(v.id, v.isPublished),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] }),
  })

  const rerunAi = useMutation({
    mutationFn: (id: string) => adminApi.rerunProblemAi(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-cards'] }),
  })

  const actionError =
    (logout.error !== null ? '로그아웃하지 못했습니다. 세션이 아직 살아 있습니다.' : null) ??
    errorMessage(transition.error) ??
    errorMessage(orgPublish.error) ??
    errorMessage(rerunAi.error)

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">카드 검수</h1>
          <p className="page-desc">
            공개 화면에는 <b>공개</b> 상태만 노출됩니다. 여기서는 모든 상태가 보입니다.
          </p>
        </div>
        <div className="admin-who">
          {me.data !== undefined && (
            <span className="muted small">
              {me.data.name} · {me.data.roleCode}
            </span>
          )}
          <button
            className="btn ghost"
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            로그아웃
          </button>
        </div>
      </div>

      {!mayPublish && (
        <div className="notice">
          읽기 권한만 있는 계정입니다. 상태 전환은 <b>card.publish</b> 권한이 있어야 합니다.
        </div>
      )}

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={t.key === tab ? 'active' : undefined}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'problem' && <DirectModeNotice mayManage={can(me.data, 'admin', 'manage')} />}
      {tab === 'org' && <OrgAutoPublishNotice mayManage={can(me.data, 'admin', 'manage')} />}

      {tab !== 'org' && (
        <div className="filters">
          <label htmlFor="admin-status-filter" className="muted small">
            검수 상태
          </label>
          <select
            id="admin-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_FILTER.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {tab === 'problem' && (
            <label className="muted small">
              <input
                type="checkbox"
                checked={needsReviewOnly}
                onChange={(e) => setNeedsReviewOnly(e.target.checked)}
              />{' '}
              확인 필요만
            </label>
          )}
        </div>
      )}

      {actionError !== null && (
        <div className="notice error" role="alert">
          {actionError}
        </div>
      )}

      {tab === 'solution' && (
        <SolutionTable
          status={status}
          mayPublish={mayPublish}
          isBusy={transition.isPending}
          onTransition={(id, to) => transition.mutate({ kind: 'solution-cards', id, to })}
        />
      )}
      {tab === 'problem' && (
        <ProblemTable
          status={status}
          needsReviewOnly={needsReviewOnly}
          mayPublish={mayPublish}
          mayWrite={mayWrite}
          isBusy={transition.isPending}
          isRerunBusy={rerunAi.isPending}
          onTransition={(id, to) => transition.mutate({ kind: 'problem-cards', id, to })}
          onRerunAi={(id) => rerunAi.mutate(id)}
        />
      )}
      {tab === 'org' && (
        <OrgTable
          mayPublish={mayPublish}
          isBusy={orgPublish.isPending}
          onToggle={(id, isPublished) => orgPublish.mutate({ id, isPublished })}
        />
      )}
    </div>
  )
}

/** 상태 전환 버튼 묶음. 서버가 준 `allowedNext` 그대로만 그린다. */
function TransitionButtons(props: {
  options: StatusOption[]
  disabled: boolean
  onPick: (to: string) => void
}) {
  if (props.options.length === 0) return <span className="muted small">전환 없음</span>
  return (
    <div className="row-actions">
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="btn ghost small-btn"
          disabled={props.disabled}
          onClick={() => props.onPick(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function SolutionTable(props: {
  status: string
  mayPublish: boolean
  isBusy: boolean
  onTransition: (id: string, to: string) => void
}) {
  const q = useQuery({
    queryKey: ['admin-cards', 'solution', props.status],
    queryFn: () => adminApi.solutionCards({ status: props.status }),
  })
  const items: AdminSolutionCard[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <table className="admin-table">
        <thead>
          <tr>
            <th>제목</th>
            <th>기업</th>
            <th>상태</th>
            <th>수정일</th>
            <th>전환</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>
                {/* 검수 중 고칠 곳이 보이면 바로 손댈 수 있어야 한다 —
                    수정 화면은 card.read면 열리고, 저장은 거기서 card.write로 다시 막힌다. */}
                <Link to={`/admin/cards/${c.id}`}>{c.title}</Link>
                <div className="muted small">{c.summary}</div>
              </td>
              <td>
                {c.org.name}
                {!c.org.isPublished && <span className="muted small"> (기업 비공개)</span>}
              </td>
              <td>{c.reviewStatusLabel}</td>
              <td className="muted small">{c.updatedAt.slice(0, 10)}</td>
              <td>
                {props.mayPublish ? (
                  <TransitionButtons
                    options={c.allowedNext}
                    disabled={props.isBusy}
                    onPick={(to) => props.onTransition(c.id, to)}
                  />
                ) : (
                  <span className="muted small">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}

/**
 * 자동 공개 설정 안내 겸 스위치의 공통 껍데기 — "왜 자동 공개가 됐/안 됐지"를 검수
 * 화면에서 설명할 수 있어야 한다. 읽기는 로그인이면 되고, 전환 버튼은 admin:manage만
 * 그린다(서버가 다시 막는다).
 */
export function BoolSettingNotice(props: {
  mayManage: boolean
  queryKey: string
  read: () => Promise<{ enabled: boolean }>
  write: (enabled: boolean) => Promise<{ enabled: boolean }>
  title: (enabled: boolean) => string
  description: string
}) {
  const queryClient = useQueryClient()
  const q = useQuery({ queryKey: [props.queryKey], queryFn: props.read })
  const m = useMutation({
    mutationFn: props.write,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [props.queryKey] }),
  })
  if (q.data === undefined) return null
  const enabled = q.data.enabled
  return (
    <div className="notice">
      <b>{props.title(enabled)}</b> — {props.description}
      {props.mayManage && (
        <>
          {' '}
          <button
            type="button"
            className="btn ghost small-btn"
            disabled={m.isPending}
            onClick={() => m.mutate(!enabled)}
          >
            {enabled ? '끄기' : '켜기'}
          </button>
        </>
      )}
      {m.error !== null && (
        <span className="muted small" role="alert">
          {' '}
          {errorMessage(m.error)}
        </span>
      )}
    </div>
  )
}

/** 다이렉트 모드(023) — 시민 문제 탭에만 그린다. */
function DirectModeNotice(props: { mayManage: boolean }) {
  return (
    <BoolSettingNotice
      mayManage={props.mayManage}
      queryKey="direct-mode"
      read={adminApi.directMode}
      write={adminApi.setDirectMode}
      title={(on) => `다이렉트 모드 ${on ? '켜짐' : '꺼짐'}`}
      description="켜져 있으면 필수 축이 채워진 시민 카드는 등록 즉시 자동 공개됩니다(워크숍 당일용). 축이 비면 켜져 있어도 여기 대기열로 옵니다."
    />
  )
}

/** 기업 자동 공개(08-25) — 기업 탭에만 그린다. 기업은 충분성 게이트 없이 등록 즉시 갈린다. */
function OrgAutoPublishNotice(props: { mayManage: boolean }) {
  return (
    <BoolSettingNotice
      mayManage={props.mayManage}
      queryKey="org-auto-publish"
      read={adminApi.orgAutoPublish}
      write={adminApi.setOrgAutoPublish}
      title={(on) => `기업 자동 공개 ${on ? '켜짐' : '꺼짐'}`}
      description="켜져 있으면 기업과 해결카드가 등록 즉시 공개됩니다(승인 생략) — 공개 폼·관리자 등록·엑셀 이관 모두 같습니다. 이 설정은 새로 등록되는 건에만 적용되고, 이미 등록된 건은 아래 목록에서 개별로 공개합니다."
    />
  )
}

/** AI 후처리 상태 라벨(023). 정본은 서버 ck_problem_ai_status의 3값이다. */
const AI_STATUS_LABEL: Record<AdminProblemCard['aiStatus'], string> = {
  pending: 'AI 대기중',
  done: 'AI 완료',
  failed: 'AI 실패',
}

function ProblemTable(props: {
  status: string
  needsReviewOnly: boolean
  mayPublish: boolean
  mayWrite: boolean
  isBusy: boolean
  isRerunBusy: boolean
  onTransition: (id: string, to: string) => void
  onRerunAi: (id: string) => void
}) {
  const q = useQuery({
    queryKey: ['admin-cards', 'problem', props.status, props.needsReviewOnly],
    queryFn: () =>
      adminApi.problemCards({
        status: props.status,
        ...(props.needsReviewOnly ? { needsReview: 'true' } : {}),
      }),
  })
  const items: AdminProblemCard[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <table className="admin-table">
        <thead>
          <tr>
            <th>제목</th>
            <th>지역</th>
            <th>접수 경로</th>
            <th>AI</th>
            <th>상태</th>
            <th>전환</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>
                {/* 제목을 누르면 제목·요약 확정 화면 — 공개 화면에 나갈 두 문장을 정하는 곳이다. */}
                <Link to={`/admin/problems/${c.id}`}>{c.title}</Link>
              </td>
              <td>{c.region.name}</td>
              <td className="muted small">{c.intakeChannel}</td>
              <td>
                <span className="muted small">{AI_STATUS_LABEL[c.aiStatus]}</span>
                {c.needsReview && (
                  <div>
                    <b className="small">확인 필요</b>
                  </div>
                )}
                {/* 재실행은 대기열 카드만 — 이미 충분한 카드에 다시 돌리면 비용만 나간다.
                    pending도 뺀다 — 등록 직후는 needs_review=true로 시작하므로(fail-safe)
                    첫 후처리가 도는 몇 초 사이 버튼이 보이면 이중 호출(이중 과금)이 된다. */}
                {props.mayWrite && c.needsReview && c.aiStatus !== 'pending' && (
                  <button
                    type="button"
                    className="btn ghost small-btn"
                    disabled={props.isRerunBusy}
                    onClick={() => props.onRerunAi(c.id)}
                  >
                    AI 다시 실행
                  </button>
                )}
              </td>
              <td>{c.reviewStatusLabel}</td>
              <td>
                {props.mayPublish ? (
                  <TransitionButtons
                    options={c.allowedNext}
                    disabled={props.isBusy}
                    onPick={(to) => props.onTransition(c.id, to)}
                  />
                ) : (
                  <span className="muted small">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}

function OrgTable(props: {
  mayPublish: boolean
  isBusy: boolean
  onToggle: (id: string, isPublished: boolean) => void
}) {
  const q = useQuery({ queryKey: ['admin-orgs'], queryFn: () => adminApi.orgs({ limit: ORG_LIST_LIMIT }) })
  const items: AdminOrg[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <table className="admin-table">
        <thead>
          <tr>
            <th>기업명</th>
            <th>유형</th>
            <th>해결카드</th>
            <th>공개</th>
            <th>전환</th>
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <tr key={o.id}>
              <td>
                <b>{o.name}</b>
              </td>
              <td className="muted small">{o.orgType}</td>
              <td>{o.cardCount}건</td>
              <td>{o.isPublished ? '공개' : '비공개'}</td>
              <td>
                {props.mayPublish ? (
                  <button
                    type="button"
                    className="btn ghost small-btn"
                    disabled={props.isBusy}
                    onClick={() => props.onToggle(o.id, !o.isPublished)}
                  >
                    {o.isPublished ? '비공개로' : '공개로'}
                  </button>
                ) : (
                  <span className="muted small">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}
